/**
 * TopNavVerifiedStrip
 *
 * The ledger-state strip from v0.17, the second row of the app header beneath the
 * nav. In the wireframe header.appbar holds topbar then status; this is the status
 * row. The verified headline is shell chrome, shown on every route, not page content.
 *
 * Source (Ruling 2 lifted, minimally). The headline facts, verdict, snapshot and
 * head, are the chain-head verification the aggregator already computes and carries
 * in summary.ledger. They are read here from a small app-wide verification endpoint,
 * given its own route, so the strip means something on Invoices and Contacts and
 * every other page, not only where the dashboard envelope is loaded. The hook
 * returns unavailable while loading, on error, and until the route lands, so the
 * strip is never wrong, only the true verdict or nothing.
 *
 * Period, FY and Last write stay progressive: optional segments, rendered only when
 * the source supplies them, never fabricated. The headline does not wait on them.
 *
 * Invariant: never a seal without live verification, never an undesigned placeholder.
 * Role tokens only. The verdict separator is a middle dot; the segment divider a
 * left rule. Height is v0.17 exactly: 8px padding on 11px mono at line-height 1.45.
 */

import { useQuery } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { verificationApi } from '@/lib/api';
import { formatDate } from '@/lib/format';

export type LedgerVerification = {
  status: 'verified' | 'failed' | 'unavailable';
  asOf: string | null; // raw ISO UTC from the route; formatted for display here
  head: string | null; // full chain head from the route; shortened for display here
  // Progressive org and period chrome. Null until the source supplies them; the
  // headline never waits on these and they are never fabricated.
  periodLabel?: string | null;
  fyLabel?: string | null;
  lastWrite?: string | null;
};

/**
 * App-wide source. Reads the chain-head verification from its own route so the shell
 * can show the strip on any page. Returns unavailable while loading, on error, or
 * until the route lands, so the strip stays dark rather than show a wrong or
 * half-built state. When the route responds, the headline lights everywhere.
 */
export function useLedgerVerification(): LedgerVerification {
  const { data } = useQuery({
    queryKey: ['ledger-verification'],
    queryFn: verificationApi.getState,
    staleTime: 60_000,
    retry: false,
  });
  const v = data?.data;
  if (!v || (v.status !== 'verified' && v.status !== 'failed')) {
    return { status: 'unavailable', asOf: null, head: null };
  }
  return {
    status: v.status,
    asOf: v.asOf,
    head: v.head,
    periodLabel: v.periodLabel ?? null,
    fyLabel: v.fyLabel ?? null,
    lastWrite: v.lastWrite ?? null,
  };
}

/** Display shortening of the full chain head: the service returns the full char(64)
 *  and leaves the display form to the consumer (here, the strip). */
const shortHead = (head: string) => `#${head.slice(0, 6)}`;

function Seal({ ok }: { ok: boolean }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} aria-hidden="true" className="w-3.5 h-3.5 shrink-0">
      <path d="M12 2l7 3v6c0 4.4-3 8-7 9-4-1-7-4.6-7-9V5z" />
      {ok ? (
        <path d="M9 12l2 2 4-4" strokeLinecap="round" strokeLinejoin="round" />
      ) : (
        <path d="M9.5 9.5l5 5m0-5l-5 5" strokeLinecap="round" />
      )}
    </svg>
  );
}

/**
 * The strip presentation, pure from props. Returns null when unavailable, which is
 * the loading, error, and pre-route state, so the component mounts nothing rather
 * than a placeholder. The verified and failed branches are provable from props now.
 */
export default function VerifiedStrip({ state }: { state: LedgerVerification }) {
  if (state.status === 'unavailable') return null;

  const ok = state.status === 'verified';
  const meta = ok
    ? [
        state.asOf ? formatDate(state.asOf) : null,
        state.head ? `head ${shortHead(state.head)}` : null,
      ]
        .filter(Boolean)
        .join(' \u00B7 ')
    : state.asOf
      ? `last good ${formatDate(state.asOf)}`
      : '';

  const seg = 'px-3.5 border-l border-edge-strong';

  return (
    <div
      className="flex flex-wrap items-center px-[26px] py-2 leading-[1.45] bg-surface-secondary border-b border-edge font-mono text-[11px] tracking-[0.06em] text-content-tertiary"
      aria-label="Ledger state"
    >
      <a
        href="#"
        aria-label="Open verification detail"
        className={cn(
          'group flex items-center gap-2 pr-3.5 rounded-sm font-medium transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
          ok ? 'text-verified hover:text-accent' : 'text-danger'
        )}
      >
        <Seal ok={ok} />
        <span>{ok ? 'Ledger verified' : 'Verification failed'}</span>
        {meta && <span className="text-content-tertiary group-hover:text-content-tertiary">{meta}</span>}
      </a>
      {state.periodLabel && <span className={seg}>{state.periodLabel}</span>}
      {state.fyLabel && <span className={seg}>{state.fyLabel}</span>}
      {state.lastWrite && <span className={seg}>Last write {state.lastWrite}</span>}
    </div>
  );
}
